import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { AgremoMapService } from '../../core/api-services/agremo-map.service';
import { AgremoAnalysisService } from '../../core/data-services/agremo-analysis.service';
import { AgremoAnalysisDetail } from '../../core/response-models/agremo-analysis-detail.model';
import { AgremoAnalysisFile } from '../../core/response-models/agremo-analysis-file.model';
import { AgremoAnalysisStatus } from '../../core/data-models/agremo-analysis-status.model';
import { AgremoStatisticsComponent } from '../../shared/components/agremo-statistics/agremo-statistics.component';
import { withChanges } from '../../core/utils/model-utils';
import { WebSocketService } from '../../web-socket-handlers/web-socket.service';

@Component({
  selector: 'app-agremo-analysis-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, AgremoStatisticsComponent],
  templateUrl: './agremo-analysis-detail.component.html',
  styleUrl: './agremo-analysis-detail.component.scss'
})
export class AgremoAnalysisDetailComponent implements OnInit, OnDestroy {
  analysis: AgremoAnalysisDetail | null = null;
  loading = true;
  errorMessage = '';
  successMessage = '';
  retryingDownload = false;
  recheckingMap = false;

  // Cached computed properties (avoid re-computation on every change detection cycle)
  downloadedFiles: AgremoAnalysisFile[] = [];
  failedFiles: AgremoAnalysisFile[] = [];
  pendingFiles: AgremoAnalysisFile[] = [];

  // WebSocket event handlers (bound references for cleanup)
  private onProgress = (event: any) => this.handleWsProgress(event);
  private onComplete = (event: any) => this.handleWsComplete(event);
  private onFailed = (event: any) => this.handleWsFailed(event);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agremoService: AgremoAnalysisService,
    private agremoMapService: AgremoMapService,
    private webSocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadAnalysis(id);
      this.setupWebSocketListeners();
    }
  }

  ngOnDestroy(): void {
    this.webSocketService.unregisterEventHandler('agremo-analysis:progress', this.onProgress);
    this.webSocketService.unregisterEventHandler('agremo-analysis:complete', this.onComplete);
    this.webSocketService.unregisterEventHandler('agremo-analysis:failed', this.onFailed);
  }

  async loadAnalysis(id: string): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.analysis = await firstValueFrom(this.agremoService.getAnalysis(id));
      if (!this.analysis) {
        this.errorMessage = 'Analysis not found';
      }
      this.computeDerivedData();
    } catch (error) {
      console.error('Failed to load analysis:', error);
      this.errorMessage = ApiError.fromHttpError(error).message;
    } finally {
      this.loading = false;
    }
  }

  private setupWebSocketListeners(): void {
    this.webSocketService.registerEventHandler('agremo-analysis:progress', this.onProgress);
    this.webSocketService.registerEventHandler('agremo-analysis:complete', this.onComplete);
    this.webSocketService.registerEventHandler('agremo-analysis:failed', this.onFailed);
  }

  private handleWsProgress(event: { analysisId: string; progress: number; status: AgremoAnalysisStatus }): void {
    if (!this.analysis || event.analysisId !== this.analysis.id) return;
    this.analysis = withChanges(this.analysis, { status: event.status, progress: event.progress });
  }

  private handleWsComplete(event: { analysisId: string; fileCount: number }): void {
    if (!this.analysis || event.analysisId !== this.analysis.id) return;
    // Reload full data to get the file records
    this.loadAnalysis(this.analysis.id);
  }

  private handleWsFailed(event: { analysisId: string; error: string }): void {
    if (!this.analysis || event.analysisId !== this.analysis.id) return;
    this.analysis = withChanges(this.analysis, { status: 'failed', errorMessage: event.error, progress: 0 });
  }

  canRecheckMap(): boolean {
    if (!this.analysis) return false;
    return this.analysis.mapStatus === 'failed';
  }

  async recheckMap(): Promise<void> {
    if (!this.analysis?.mapId || this.recheckingMap) return;

    this.recheckingMap = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.agremoMapService.recheckMap(this.analysis.mapId));
      this.successMessage = 'Map recheck queued successfully';
      await this.loadAnalysis(this.analysis.id);
    } catch (error) {
      console.error('Failed to recheck map:', error);
      this.errorMessage = ApiError.fromHttpError(error).message;
    } finally {
      this.recheckingMap = false;
    }
  }

  canRetryDownload(): boolean {
    if (!this.analysis) return false;
    return !!this.analysis.agremoAnalysisId &&
      (this.analysis.status === 'completed' || this.analysis.status === 'failed' || this.analysis.status === 'downloading');
  }

  async retryDownload(): Promise<void> {
    if (!this.analysis || this.retryingDownload) return;

    this.retryingDownload = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.agremoService.retryDownload(this.analysis.id));
      this.successMessage = 'Download retry queued successfully';
      // Reload analysis to see updated status
      await this.loadAnalysis(this.analysis.id);
    } catch (error) {
      console.error('Failed to retry download:', error);
      this.errorMessage = ApiError.fromHttpError(error).message;
    } finally {
      this.retryingDownload = false;
    }
  }

  private computeDerivedData(): void {
    const files = this.analysis?.files || [];
    this.downloadedFiles = files.filter(f => f.downloadStatus === 'downloaded' && f.downloadUrl);
    this.failedFiles = files.filter(f => f.downloadStatus === 'failed');
    this.pendingFiles = files.filter(f => f.downloadStatus === 'pending');
  }

  get hasStatistics(): boolean {
    return !!this.analysis?.statistics &&
      Object.keys(this.analysis.statistics).length > 0;
  }

  getFileTypeLabel(fileType: string): string {
    const labels: Record<string, string> = {
      pdf: 'PDF Report',
      tiff: 'GeoTIFF',
      shp: 'Shapefile',
      png: 'Map Image',
    };
    return labels[fileType] || fileType.toUpperCase();
  }

  getFileTypeIcon(fileType: string): string {
    const icons: Record<string, string> = {
      pdf: 'bi-file-earmark-pdf',
      tiff: 'bi-file-earmark-image',
      shp: 'bi-file-earmark-zip',
      png: 'bi-file-earmark-image',
    };
    return icons[fileType] || 'bi-file-earmark';
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'processing': return 'bg-info';
      case 'downloading': return 'bg-info text-dark';
      case 'queued': return 'bg-primary';
      case 'pending': return 'bg-warning text-dark';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      case 'downloading': return 'Downloading Results';
      case 'queued': return 'Queued';
      case 'pending': return 'Pending';
      case 'failed': return 'Failed';
      default: return status ?? '-';
    }
  }

  getDownloadStatusBadgeClass(status: string): string {
    switch (status) {
      case 'downloaded': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  formatDateTime(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  goBack(): void {
    this.router.navigate(['/agremo-analyses']);
  }
}
